// ============================================================
// v3 — client photo prep. Re-encodes a picked/captured image
// through a canvas at ≤ 800 px and emits a JPEG data URL. The
// canvas re-encode DROPS all metadata (incl. GPS EXIF) — this is
// a privacy requirement, not just bandwidth (§5 / §20.9).
// ============================================================

const MAX_EDGE = 800;
const QUALITY = 0.8;

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      /* Safari/older — fall back to an <img> */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    await new Promise<void>((res, rej) => {
      img.onload = () => res();
      img.onerror = () => rej(new Error('decode failed'));
      img.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Compress an image File to a metadata-free JPEG data URL (≤ MAX_EDGE px). */
export async function compressImage(file: File, maxEdge = MAX_EDGE, quality = QUALITY): Promise<string> {
  const bmp = await loadBitmap(file);
  const w0 = 'width' in bmp ? bmp.width : (bmp as HTMLImageElement).naturalWidth;
  const h0 = 'height' in bmp ? bmp.height : (bmp as HTMLImageElement).naturalHeight;
  const scale = Math.min(1, maxEdge / Math.max(w0, h0 || 1));
  const w = Math.max(1, Math.round(w0 * scale));
  const h = Math.max(1, Math.round(h0 * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  ctx.drawImage(bmp as CanvasImageSource, 0, 0, w, h);
  if ('close' in bmp) (bmp as ImageBitmap).close();
  // toDataURL re-encodes from raw pixels → no EXIF/GPS survives.
  return canvas.toDataURL('image/jpeg', quality);
}
